import { FC, MouseEvent, useState } from "react";
import styled from "styled-components";

import { StockLibraryImageResource } from "@/types/resources";
import Text from "@/components/Text";
import { ActionsDropdownButton } from "@/components/ActionsDropdown";
import { ReactComponent as FolderIcon } from "@/assets/icons/folder.svg";

const Img = styled.img`
  width: 100%;
  display: block;
`;

const StyledActionsDropdownButton = styled(ActionsDropdownButton)`
  height: 48px;
`;

const DownloadCount = styled(Text)`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 100px;
  height: 27px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  svg {
    margin-right: 6px;
  }
`;

const DownloadButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 60px;
  bottom: 16px;
  transition: all ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  opacity: 0;
`;

const LightBoxButton = styled(DownloadButton)`
  left: 16px;
`;

const MoreButtonHolder = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  opacity: 0;
  transition: all ease-out 0.2s;
`;

const MoreButton = styled(DownloadButton)`
  position: static;
`;

const Dropdown = styled.div`
  position: absolute;
  bottom: 40px;
  width: 290px;
  right: 0;
  padding: 8px 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
`;

const QueryBox = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

const QueryText = styled(Text)`
  font-weight: 500;
  display: flex;
  gap: 4px;
  align-items: center;
  span {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const Overlay = styled.div`
  background: rgba(0, 0, 0, 0.12);
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: opacity ease-out 0.2s;
`;

const ImageHolder = styled.div`
  position: relative;
  &:hover {
    ${Overlay}, ${DownloadButton}, ${MoreButtonHolder} {
      opacity: 1;
    }
  }
`;

interface Props {
  image: StockLibraryImageResource;
  onClick: () => void;
}

const Result: FC<Props> = ({ image, onClick }) => {
  const [showMJDropdown, setShowMjDropdown] = useState(false);

  const [downloadsCount, setDownloadsCount] = useState(
    image.meta!.downloads ?? 0
  );

  const onDownload = (e: MouseEvent<HTMLAnchorElement>) => {
    if (image.meta!.licensed) {
      e.preventDefault();
      window.open(image.meta!.pageUrl, "_blank");
      return;
    }
    setDownloadsCount((prev) => prev + 1);
  };

  return (
    <div>
      <ImageHolder>
        <Img
          src={image.versions.preview ?? image.versions.original}
          loading="lazy"
          alt={image.name}
        />
        <Overlay />
        <DownloadButton
          as="a"
          download
          href={image.localUrl}
          onClick={onDownload}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.6354 10.0008C16.6354 13.665 13.665 16.6354 10.0008 16.6354C6.33663 16.6354 3.36621 13.665 3.36621 10.0008C3.36621 6.33663 6.33663 3.36621 10.0008 3.36621C13.665 3.36621 16.6354 6.33663 16.6354 10.0008Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeMiterlimit="10"
            />
            <path
              d="M7.38965 10.4668L10.0005 13.0776L12.6113 10.4668"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 6.92383V13.0777"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </DownloadButton>
        <LightBoxButton onClick={onClick}>
          <svg
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.57888 14.5383C11.1335 14.5383 14.0152 11.6454 14.0152 8.07677C14.0152 4.50816 11.1335 1.61523 7.57888 1.61523C4.02421 1.61523 1.14258 4.50816 1.14258 8.07677C1.14258 11.6454 4.02421 14.5383 7.57888 14.5383Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12.1309 12.646L15.8548 16.3845"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </LightBoxButton>
        <MoreButtonHolder>
          <MoreButton onClick={() => setShowMjDropdown((prev) => !prev)}>
            {showMJDropdown ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <rect
                  width="12"
                  height="12"
                  transform="translate(0.515625 9) rotate(-45)"
                  fill="#F8F8F8"
                />
                <path
                  d="M5.36523 5.36328L12.6383 12.6364"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.36523 12.6367L12.6383 5.36362"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <rect width="12" height="12" fill="#F8F8F8" />
                <path
                  d="M6 0.857422V11.1431"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M0.857422 6H11.1431"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </MoreButton>
          {showMJDropdown && (
            <Dropdown>
              <StyledActionsDropdownButton
                as="a"
                href={`/general/image-tool/${image.model_id}`}
                target="_blank"
              >
                <span>
                  <FolderIcon />
                </span>
                Go to image project
              </StyledActionsDropdownButton>
            </Dropdown>
          )}
        </MoreButtonHolder>
        <DownloadCount $type="smallHeadline">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.23047 7.80724L9.69201 1.3457"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.69276 6.88416V1.3457H4.1543"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M1.38477 10.5762H10.6155"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {downloadsCount}
        </DownloadCount>
      </ImageHolder>
      {!!image.meta?.query && (
        <QueryBox>
          <QueryText $type="smallBold">
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M7.99972 14.8569C11.7868 14.8569 14.8569 11.7868 14.8569 7.99972C14.8569 4.21263 11.7868 1.14258 7.99972 1.14258C4.21263 1.14258 1.14258 4.21263 1.14258 7.99972C1.14258 11.7868 4.21263 14.8569 7.99972 14.8569Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.00056 9.71415V9.14272C8.45263 9.14272 8.89455 9.00866 9.27043 8.75751C9.64632 8.50635 9.93928 8.14937 10.1123 7.73171C10.2853 7.31405 10.3305 6.85447 10.2424 6.41108C10.1542 5.9677 9.93646 5.56042 9.6168 5.24076C9.29714 4.9211 8.88986 4.7034 8.44648 4.61521C8.00309 4.52701 7.54351 4.57228 7.12585 4.74528C6.70819 4.91828 6.35121 5.21125 6.10006 5.58713C5.8489 5.96301 5.71484 6.40493 5.71484 6.857"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.99972 12.8569C8.47311 12.8569 8.85686 12.4731 8.85686 11.9997C8.85686 11.5263 8.47311 11.1426 7.99972 11.1426C7.52633 11.1426 7.14258 11.5263 7.14258 11.9997C7.14258 12.4731 7.52633 12.8569 7.99972 12.8569Z"
                  fill="currentColor"
                />
              </svg>
              Prompt:
            </span>{" "}
            {image.meta.query}
          </QueryText>
        </QueryBox>
      )}
    </div>
  );
};

export default Result;
